import { ChatCircleDots } from '@phosphor-icons/react';
import { useDateFormat, useProperty, useSnowplowTracking, useTranslation } from '@eti/providers';
import type { HeadingTypes } from '@etg/wings';
import {
  Badge,
  breakpoints,
  Button,
  Card,
  Heading,
  Inline,
  Stack,
  Text,
  textVariants,
  vars,
} from '@etg/wings';
import { css, cx, etiColors } from '@eti/styles';
import { FormProvider, useForm } from 'react-hook-form';
import { useChat } from '../../common/chat/ChatContext';
import type { ChatType } from '../../common/chat/models';
import { isRequired } from '../../common/components/formComponents/validations';
import FormCheckbox from '../../common/components/formComponents/formCheckbox/FormCheckbox';
import ContactSchedule from './ContactSchedule';
import { getLocalSchedule } from './utils';
import type { ScheduleType } from './models';

const cardStyles = css`
  background-color: #fafafa;
  border: 1px solid ${vars.colors.divider};
`;

const badgeStyles = css`
  order: 1;
`;

const chatTitleStyles = css`
  order: 2;
`;

interface ChatProps {
  className?: string;
  shouldShowHeader?: boolean;
  chatType: ChatType;
  snowplowProperty?: string;
  headingAs?: `h${HeadingTypes['level']}` | 'h5';
}

interface ChatHeaderProps {
  className?: string;
  headingAs?: `h${HeadingTypes['level']}` | 'h5';
}

export const ChatHeader = ({ className, headingAs = 'h3' }: ChatHeaderProps) => {
  const { t } = useTranslation();

  return (
    <Stack spacing={16}>
      <Inline alignY="center" className={cx(className, chatTitleStyles)} spacing={8}>
        <ChatCircleDots
          aria-hidden="true"
          color={etiColors.black}
          data-testid="chat-icon"
          size={24}
        />
        <Heading as={headingAs} data-testid="chat-title" level={3}>
          {t('ContactUs.ContactSection.Chat.Title')}
        </Heading>
      </Inline>
      <Inline className={badgeStyles}>
        <Badge data-testid="chat-badge">{t('Chat.Badge.Text')}</Badge>
      </Inline>
    </Stack>
  );
};

const Chat = ({
  chatType,
  className,
  shouldShowHeader = true,
  snowplowProperty,
  headingAs,
}: ChatProps) => {
  const { p } = useProperty();
  const { t } = useTranslation();
  const { time } = useDateFormat();
  const { isSnowplowTrackingEnabled, sendTrackEvent } = useSnowplowTracking();
  const methods = useForm<{ chatConsent: boolean }>({
    defaultValues: { chatConsent: false },
  });

  const { handleSubmit } = methods;

  const { openChat } = useChat();
  const schedule: ScheduleType[] = getLocalSchedule(p('ContactUs.Chat.Schedule'), t, time);

  const handleOnButtonClick = () => {
    if (isSnowplowTrackingEnabled && snowplowProperty) {
      sendTrackEvent({
        category: 'Order Details',
        action: 'Click',
        label: 'CHAT_START',
        property: snowplowProperty,
      });
    }
    openChat(chatType);
  };

  return (
    <Card className={cx(cardStyles, className)} data-testid="chat-section">
      <Stack spacing={16}>
        {shouldShowHeader && <ChatHeader headingAs={headingAs} />}
        <Text data-testid="chat-schedule-description">
          {t('ContactUs.ContactSection.Schedule.Description')}
        </Text>
        <Stack spacing={8}>
          <>
            <Text data-testid="chat-english-schedule-title" variant={textVariants._14_BOLD}>
              {t('ContactUs.ContactSection.EnglishSchedule.Title')}
            </Text>
            <Text data-testid="chat-english-schedule-description">
              {t('ContactUs.ContactSection.Chat.EnglishSchedule.Text')}
            </Text>
          </>
          {schedule?.length > 0 && <ContactSchedule isChatSchedule schedule={schedule} />}
        </Stack>
        <FormProvider {...methods}>
          <form noValidate onSubmit={handleSubmit(handleOnButtonClick)}>
            <Stack>
              <FormCheckbox
                isRequired
                label={t('ContactUs.ContactSection.Chat.ConsentCheckbox.Label')}
                name="chatConsent"
                validations={isRequired(t('Validation.Required'))}
              />
              <Inline align="start" collapseBelow={breakpoints._768} noWrap>
                <Button data-testid="chat-button" type="submit" variant="primary">
                  {t('ContactUs.ContactSection.Chat.Button')}
                </Button>
              </Inline>
            </Stack>
          </form>
        </FormProvider>
      </Stack>
    </Card>
  );
};

export default Chat;
